/**
 * Blended fuzzy-search ranker for the slash-command (`/`) lookup dropdown.
 *
 * Pure functions, no React. Given a user-typed query and a list of LookupItems
 * (column_name + entity_name pairs from the backend), returns the top N scored
 * matches over `entity_name` along with the matched character ranges so the
 * dropdown can highlight the hit characters.
 *
 * The final score combines several sub-algorithms (max of the sub-scores plus
 * an optional Levenshtein typo-bonus):
 *
 *   - Exact-equal               : 1.00
 *   - Prefix (case-insensitive) : 0.85
 *   - Token-boundary prefix     : 0.75  (split on whitespace, `-`, `_`)
 *   - Contiguous substring      : 0.55 - (pos / len) * 0.10  (earlier wins)
 *   - Subsequence (with gaps)   : 0.35 - gaps * 0.02
 *   - Levenshtein bonus         : +(0.20 / dist) when |q| <= 6 and dist <= 2
 *
 * Tie-breakers: shorter `entity_name` first, then alphabetical.
 */

import type { LookupItem } from '../AskAsperAgentic.types';
export type { LookupItem };

export interface MatchRange {
    /** Inclusive start char index in `entity_name`. */
    start: number;
    /** Exclusive end char index in `entity_name`. */
    end: number;
}

export interface ScoredMatch {
    item: LookupItem;
    score: number;
    ranges: MatchRange[];
}

interface SubScore {
    score: number;
    ranges: MatchRange[];
}

// ---------- helpers ----------

const TOKEN_SEP_RE = /[\s_\-]+/g;

function mergeRanges(ranges: MatchRange[]): MatchRange[] {
    if (ranges.length <= 1) return ranges;
    const sorted = [...ranges].sort((a, b) => a.start - b.start);
    const merged: MatchRange[] = [{ ...sorted[0] }];
    for (let i = 1; i < sorted.length; i++) {
        const last = merged[merged.length - 1];
        const cur = sorted[i];
        if (cur.start <= last.end) {
            last.end = Math.max(last.end, cur.end);
        } else {
            merged.push({ ...cur });
        }
    }
    return merged;
}

/** Iterative two-row Levenshtein distance. */
function levenshtein(a: string, b: string): number {
    const al = a.length;
    const bl = b.length;
    if (al === 0) return bl;
    if (bl === 0) return al;
    let prev = new Array<number>(bl + 1);
    let curr = new Array<number>(bl + 1);
    for (let j = 0; j <= bl; j++) prev[j] = j;
    for (let i = 1; i <= al; i++) {
        curr[0] = i;
        for (let j = 1; j <= bl; j++) {
            const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
            curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
        }
        [prev, curr] = [curr, prev];
    }
    return prev[bl];
}

// ---------- sub-scorers ----------

function scoreExact(q: string, name: string): SubScore | null {
    if (q === name) {
        return { score: 1.0, ranges: [{ start: 0, end: name.length }] };
    }
    return null;
}

function scorePrefix(q: string, name: string): SubScore | null {
    if (name.startsWith(q)) {
        return { score: 0.85, ranges: [{ start: 0, end: q.length }] };
    }
    return null;
}

function scoreTokenPrefix(q: string, name: string): SubScore | null {
    const tokenStarts: number[] = [];
    let m: RegExpExecArray | null;
    const re = new RegExp(TOKEN_SEP_RE.source, 'g');
    while ((m = re.exec(name)) !== null) {
        tokenStarts.push(m.index + m[0].length);
    }
    for (const start of tokenStarts) {
        if (start + q.length > name.length) continue;
        if (name.substr(start, q.length) === q) {
            return { score: 0.75, ranges: [{ start, end: start + q.length }] };
        }
    }
    return null;
}

function scoreSubstring(q: string, name: string): SubScore | null {
    const idx = name.indexOf(q);
    if (idx < 0) return null;
    const positionPenalty = (idx / Math.max(name.length, 1)) * 0.1;
    return { score: 0.55 - positionPenalty, ranges: [{ start: idx, end: idx + q.length }] };
}

function scoreSubsequence(q: string, name: string): SubScore | null {
    let qi = 0;
    let lastMatched = -1;
    let gaps = 0;
    const ranges: MatchRange[] = [];
    for (let ni = 0; ni < name.length && qi < q.length; ni++) {
        if (name.charCodeAt(ni) === q.charCodeAt(qi)) {
            if (lastMatched >= 0 && ni - lastMatched > 1) gaps += 1;
            ranges.push({ start: ni, end: ni + 1 });
            lastMatched = ni;
            qi += 1;
        }
    }
    if (qi !== q.length) return null;
    const score = Math.max(0.05, 0.35 - gaps * 0.02);
    return { score, ranges: mergeRanges(ranges) };
}

function levBonus(q: string, name: string): number {
    if (q.length === 0 || q.length > 6) return 0;
    const distFull = levenshtein(q, name);
    if (distFull <= 2) return 0.2 / Math.max(distFull, 1);
    // Try first token as a fallback (covers cases like q="cocaa" vs "Coca Cola")
    const firstSepIdx = name.search(/[\s_\-]/);
    if (firstSepIdx > 0) {
        const firstToken = name.slice(0, firstSepIdx);
        const distFirst = levenshtein(q, firstToken);
        if (distFirst <= 2) return 0.2 / Math.max(distFirst, 1);
    }
    return 0;
}

// ---------- public API ----------

/**
 * Score a single entity name against the user query. Returns null when there is
 * no recognizable match by any sub-algorithm.
 */
export function scoreItem(
    query: string,
    entityName: string,
): { score: number; ranges: MatchRange[] } | null {
    const q = query.toLowerCase();
    if (q.length === 0) return null;
    const name = entityName.toLowerCase();

    const subs: Array<SubScore | null> = [
        scoreExact(q, name),
        scorePrefix(q, name),
        scoreTokenPrefix(q, name),
        scoreSubstring(q, name),
        scoreSubsequence(q, name),
    ];

    let best: SubScore | null = null;
    for (const s of subs) {
        if (s && (!best || s.score > best.score)) best = s;
    }

    const bonus = levBonus(q, name);
    if (!best) {
        if (bonus > 0) return { score: bonus, ranges: [] };
        return null;
    }
    return { score: best.score + bonus, ranges: best.ranges };
}

/**
 * Run the blended fuzzy search and return the top `maxResults` matches.
 *
 * When the query is empty, returns the first `maxResults` items unscored so the
 * dropdown is non-empty the moment the user types `/`.
 */
export function searchLookup(
    query: string,
    items: LookupItem[],
    maxResults = 7,
): ScoredMatch[] {
    const q = query.trim();
    if (!items || items.length === 0) return [];

    if (q.length === 0) {
        return items.slice(0, maxResults).map((it) => ({ item: it, score: 0, ranges: [] }));
    }

    const matches: ScoredMatch[] = [];
    for (const item of items) {
        const r = scoreItem(q, item.entity_name);
        if (r) matches.push({ item, score: r.score, ranges: r.ranges });
    }

    matches.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        const la = a.item.entity_name.length;
        const lb = b.item.entity_name.length;
        if (la !== lb) return la - lb;
        return a.item.entity_name.localeCompare(b.item.entity_name);
    });

    return matches.slice(0, maxResults);
}
